import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import clsx from 'clsx';

import Toaster from 'components/Toaster';
import Loader from 'components/Loader';
import TabTable from 'components/TabTable';
import useIsMobile from 'utils/useIsMobile';

import { getReferences } from './api/getReferences';
import { getCategoryList } from './api/getCategoryList';
import { getCategoryTable } from './api/getCategoryTable';

import styles from './style.module.scss';

const ManualPage = () => {
  const { language } = i18n;
  const { t } = useTranslation('global');
  const isMobile = useIsMobile();
  const [references, setReferences] = useState([]);
  const [category, setCategory] = useState([]);
  const [table, setTable] = useState([]);
  const [selectedTable, setSelectedTable] = useState();
  const [selectedTab, setSelectedTab] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleGetCategoryTable = useCallback(async categoryId => {
    const categoryTable = await getCategoryTable(categoryId);

    if (categoryTable.data) {
      setTable(categoryTable.data);
    }
  }, []);

  const handleGetCategoryList = useCallback(
    async referencesId => {
      const categoryList = await getCategoryList(referencesId);

      if (categoryList.data) {
        if (Array.isArray(categoryList.data)) {
          setCategory(categoryList.data.sort((a, b) => a.id - b.id));
          setSelectedTable(categoryList.data[0].id);
          handleGetCategoryTable(categoryList.data[0].id);
        }
      }

      if (!categoryList.data.length) {
        setCategory([]);
        setSelectedTable();
      }
    },
    [handleGetCategoryTable],
  );

  const handleCategoryChange = categoryId => {
    handleGetCategoryTable(categoryId);
    setSelectedTable(categoryId);
  };

  const handleTabChange = referencesId => {
    setSelectedTab(referencesId);
    handleGetCategoryList(referencesId);
  };

  const handleRadioChange = referenceId => {
    handleTabChange(referenceId);
  };

  const handleGetReferences = useCallback(async () => {
    try {
      setIsLoading(true);
      const referenceDetails = await getReferences();

      if (referenceDetails.data) {
        if (Array.isArray(referenceDetails.data)) {
          setReferences(referenceDetails.data.sort((a, b) => a.id - b.id));
          setSelectedTab(referenceDetails.data[0].id);
          handleGetCategoryList(referenceDetails.data[0].id);
        }

        setIsLoading(false);
      }
    } catch (err) {
      setIsError(true);
      setIsLoading(false);
    }
  }, [setSelectedTab, handleGetCategoryList]);

  useEffect(() => {
    if (language) {
      handleGetReferences();
    }
  }, [language, handleGetReferences]);

  return (
    <div className={isMobile ? styles.containerMobile : styles.container}>
      {isError && <Toaster />}
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <div className={styles.header}>
            <div className={styles.heading}>
              <h1>{t('refHeader')}</h1>
            </div>
            <div className={styles.heading_buttons}>
              {references?.map(reference => (
                <div
                  aria-hidden
                  key={reference.id}
                  onClick={() => {
                    handleTabChange(reference.id);
                  }}>
                  <label
                    htmlFor={`tab-${reference.id}`}
                    className={`${styles.radioLabel} ${
                      selectedTab === reference.id && styles.radioChecked
                    }`}>
                    <input
                      type="radio"
                      name="tab"
                      id={`tab-${reference.id}`}
                      value={reference.name}
                      checked={selectedTab === reference.id}
                      onChange={() => handleRadioChange(reference.id)}
                    />
                    <span className={styles.radioText}>{reference.name}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.manual_content}>
            <div className={styles.buttons}>
              {category?.map(tableName => (
                <div key={`${tableName.id}-${tableName.name}`}>
                  <button
                    type="button"
                    onClick={() => handleCategoryChange(tableName.id)}
                    className={clsx(
                      selectedTable === tableName.id && styles.activeButton,
                    )}>
                    {tableName.name}
                  </button>
                </div>
              ))}
            </div>

            <div className={styles.tablelayot}>
              {selectedTable && <TabTable key={table.id} data={table} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManualPage;
